import React from 'react';
import { Tag, Popover } from 'antd/lib';
import type { TagProps } from 'antd/lib/tag';
import type { PopoverProps } from 'antd/lib/popover';
import styles from './HoverPreviewTag.module.css';
import useTranslation from '@hooks/useTranslation';

type HoverPreviewTagProps = {
  /** Props to pass to the Ant Design Tag component */
  tagProps: TagProps;
  /** Props to pass to the Ant Design Popover component */
  popoverProps?: Omit<PopoverProps, 'content' | 'title'>;
  /** The content string to display in the Popover preview */
  previewContent: string;
  /** The title for the Popover preview */
  previewTitle: string;
  /** Whether to show the 'Click tag to add to context' message in the Popover. Defaults to false. */
  showAddToContextMessage?: boolean;
  /** The children to render inside the Tag component (usually the tag's text) */
  children: React.ReactNode;
};

/**
 * @function HoverPreviewTag
 * @description A generic component that renders an Ant Design Tag.
 * On hover, it displays a Popover with a preview of the provided content.
 * @param {HoverPreviewTagProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered HoverPreviewTag component.
 */
const HoverPreviewTag = (props: HoverPreviewTagProps): React.ReactElement => {
  const {
    tagProps,
    popoverProps,
    previewContent,
    previewTitle,
    showAddToContextMessage = false,
    children,
  } = props;
  const t = useTranslation('LogicMapper.Gates.Script.Settings.Assistant');


  /**
   * @function renderTagContentPreview
   * @description Renders the content for the Popover preview.
   * @param {string} content - The text content to display.
   * @param {boolean} [alreadyAdded] - If true, hides the 'Click to add' message.
   * @returns {React.ReactElement} The preview content element.
   */
  const renderTagContentPreview = (content: string, alreadyAdded?: boolean): React.ReactElement => (
    <>
      <pre className={styles.PopoverContentPreview}>
        {content}
      </pre>
      {!alreadyAdded && <div className={styles.ClickTagToAddToContext}>{t('ClickTagToAddToContext')}</div>}
    </>
  );

  return (
    <Popover
      content={renderTagContentPreview(previewContent, !showAddToContextMessage)}
      title={previewTitle}
      overlayClassName={styles.PreviewPopover}
      mouseEnterDelay={0.3}
      placement="left"
      {...popoverProps}
    >
      <Tag {...tagProps}>
        {children}
      </Tag>
    </Popover>
  );
};

export default HoverPreviewTag;
